<template>
    <BaseNotification v-if="(error || requestFailed) && statusCode !== 404" theme="error" hide-dismiss :text="t('chat.performancePicker.documents.loadFailed')" class="mx-4 my-2">
        <template #content
            ><span>{{ t("chat.performancePicker.documents.loadFailed") }}</span
            ><BaseButton theme="neutral" size="sm" @click="prepare">{{ t("common.tryAgain") }}</BaseButton></template
        >
    </BaseNotification>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ReviewDocumentCardRequest, ReviewDocumentCardResponse } from "~vue/api/generated/chat";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import type { ChatMessage } from "~vue/types/chat";
import { logError } from "~vue/utils/logUtils";
import { onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ sessionUuid: string; reviewConversationId: string; messages: ChatMessage[] }>();
const emit = defineEmits<{ ready: [card: ReviewDocumentCardResponse]; allowed: [value: boolean] }>();
const { t } = useI18n();
const payload = ref<ReviewDocumentCardRequest>({ review_conversation_id: props.reviewConversationId });
const { data, execute, error, statusCode } = useFetch(() => `/v1/chat/performance-management/${props.sessionUuid}/document-card`, { immediate: false })
    .post(payload)
    .json<ReviewDocumentCardResponse>();
const {
    data: dismissedCard,
    execute: executeDismiss,
    error: dismissError,
} = useFetch(() => `/v1/chat/performance-management/${props.sessionUuid}/document-card/dismiss`, { immediate: false })
    .post(payload)
    .json<ReviewDocumentCardResponse>();
const requestFailed = ref(false);
let readyCard: ReviewDocumentCardResponse | null = null;
let dismissInFlight = false;
let active = true;
let inFlight: Promise<void> | null = null;
let terminal = false;
let refreshRequested = false;
onUnmounted(() => {
    active = false;
});

async function fetchCard() {
    do {
        refreshRequested = false;
        requestFailed.value = false;
        try {
            await execute();
        } catch (failure) {
            requestFailed.value = true;
            logError(failure, { context: { operation: "review_document_card" } });
            return;
        }
        if (!active) {
            return;
        }
        if (statusCode.value === 404) {
            terminal = true;
            return;
        }
        if (error.value || !data.value) {
            return;
        }
        const card = data.value;
        emit("allowed", card.status !== "unavailable");
        if (card.status === "ready") {
            terminal = true;
            readyCard = card;
            emit("ready", card);
            void dismissAfterSend();
        } else if (card.status === "unavailable") {
            terminal = true;
        }
    } while (refreshRequested && !terminal);
}
async function dismissAfterSend() {
    const messageId = readyCard?.message_id;
    if (
        !active ||
        dismissInFlight ||
        readyCard?.dismissed ||
        messageId == null ||
        !props.messages.some((message) => message.role === "user" && message.chat_message_id != null && message.chat_message_id > messageId)
    ) {
        return;
    }
    dismissInFlight = true;
    try {
        await executeDismiss();
        if (!active) {
            return;
        }
        if (dismissError.value || !dismissedCard.value) {
            throw dismissError.value ?? new Error("Review document card dismissal returned no result");
        }
        if (dismissedCard.value.status === "unavailable") {
            emit("allowed", false);
            return;
        }
        readyCard = dismissedCard.value;
        emit("ready", readyCard);
    } catch (failure) {
        logError(failure, { context: { operation: "review_document_card_dismiss_after_send" } });
    } finally {
        dismissInFlight = false;
    }
}
async function prepare() {
    if (terminal || !active) {
        return;
    }
    if (inFlight) {
        refreshRequested = true;
        return inFlight;
    }
    inFlight = fetchCard();
    try {
        await inFlight;
    } finally {
        inFlight = null;
    }
}
watch(
    () =>
        props.messages
            .filter((message) => message.role === "assistant" && message.chat_message_id && message.lines.some((line) => line.type === "md" && line.content.trim()))
            .map((message) => message.chat_message_id)
            .join(","),
    () => {
        void prepare();
    },
);
watch(
    () =>
        props.messages
            .filter((message) => message.role === "user" && message.chat_message_id)
            .map((message) => message.chat_message_id)
            .join(","),
    () => {
        void dismissAfterSend();
    },
);
defineExpose({ prepare });
</script>
